import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './PopupSettings.css';

const API_ORIGIN = 'https://valleyapi-dr9y.onrender.com';
const POPUP_API = `${API_ORIGIN}/api/admin/site-popup`;
const POPUP_POSITIONS = [
  { value: 'top-left', label: 'Top left' },
  { value: 'top-center', label: 'Top center' },
  { value: 'top-right', label: 'Top right' },
  { value: 'center-left', label: 'Center left' },
  { value: 'center', label: 'Center' },
  { value: 'center-right', label: 'Center right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-center', label: 'Bottom center' },
  { value: 'bottom-right', label: 'Bottom right' },
];
const VALID_POSITIONS = new Set(POPUP_POSITIONS.map(({ value }) => value));

const emptySettings = {
  enabled: false,
  blur_background: true,
  link_url: '',
  image_path: null,
  position: 'center',
};

const normalizePosition = (position) => VALID_POSITIONS.has(position) ? position : 'center';

const imageUrl = (path) => {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

export default function PopupSettings() {
  const [settings, setSettings] = useState(emptySettings);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadSettings = async () => {
      try {
        const response = await fetch(POPUP_API, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
          cache: 'no-store',
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not load popup settings');

        setSettings({
          enabled: Boolean(data.enabled),
          blur_background: Boolean(data.blur_background),
          link_url: data.link_url || '',
          image_path: data.image_path || null,
          position: normalizePosition(data.position),
        });
        setPreview(imageUrl(data.image_path));
      } catch (error) {
        if (error.name !== 'AbortError') toast.error(error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadSettings();
    return () => controller.abort();
  }, []);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Use a JPG, PNG, GIF or WEBP image');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must not exceed 5MB');
      event.target.value = '';
      return;
    }

    setSelectedImage(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setSelectedImage(null);
    setPreview(null);
    setRemoveImage(Boolean(settings.image_path));
    setSettings((current) => ({ ...current, enabled: false }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (settings.enabled && !selectedImage && (!settings.image_path || removeImage)) {
      toast.error('Upload an image before enabling the popup');
      return;
    }

    setSaving(true);
    try {
      const form = new FormData();
      form.set('enabled', String(settings.enabled));
      form.set('blur_background', String(settings.blur_background));
      form.set('link_url', settings.link_url.trim());
      form.set('position', settings.position);
      form.set('remove_image', String(removeImage));
      if (selectedImage) form.set('image', selectedImage);

      const response = await fetch(POPUP_API, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: form,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save popup settings');

      const nextSettings = {
        enabled: Boolean(data.enabled),
        blur_background: Boolean(data.blur_background),
        link_url: data.link_url || '',
        image_path: data.image_path || null,
        position: normalizePosition(data.position),
      };
      setSettings(nextSettings);
      setPreview(imageUrl(data.image_path));
      setSelectedImage(null);
      setRemoveImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Popup settings saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="content-loading"><div className="spinner" /><p>Loading popup settings...</p></div>;
  }

  return (
    <form className="popup-settings" onSubmit={handleSubmit}>
      <section className="popup-settings__form" aria-labelledby="popup-settings-title">
        <div className="popup-settings__heading">
          <div>
            <h2 id="popup-settings-title">Website pop-up</h2>
            <p>Upload the promotional image visitors will see when they enter the website.</p>
          </div>
          <label className="popup-switch">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(event) => setSettings((current) => ({ ...current, enabled: event.target.checked }))}
              disabled={!preview}
            />
            <span aria-hidden="true" />
            Show pop-up
          </label>
        </div>

        <div className="popup-field">
          <label htmlFor="popup-link">Destination link</label>
          <input
            id="popup-link"
            type="text"
            value={settings.link_url}
            onChange={(event) => setSettings((current) => ({ ...current, link_url: event.target.value }))}
            placeholder="https://example.com/promotion or /brands"
            maxLength={2048}
          />
          <small>Optional. Clicking the image will open this URL or website path.</small>
        </div>

        <label className="popup-check">
          <input
            type="checkbox"
            checked={settings.blur_background}
            onChange={(event) => setSettings((current) => ({ ...current, blur_background: event.target.checked }))}
          />
          <span>
            <strong>Blur page background</strong>
            <small>Adds a soft blur behind the pop-up so the image stands out.</small>
          </span>
        </label>

        <fieldset className="popup-position">
          <legend>Pop-up position</legend>
          <small>Choose where the promotion will appear on the visitor's screen.</small>
          <div className="popup-position__grid">
            {POPUP_POSITIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={settings.position === value ? 'is-selected' : ''}
                aria-label={label}
                aria-pressed={settings.position === value}
                title={label}
                onClick={() => setSettings((current) => ({ ...current, position: value }))}
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
          <strong>{POPUP_POSITIONS.find(({ value }) => value === settings.position)?.label}</strong>
        </fieldset>

        <div className="popup-field">
          <label htmlFor="popup-image">Pop-up image</label>
          <label className="popup-upload" htmlFor="popup-image">
            <i className="fas fa-cloud-upload-alt" aria-hidden="true" />
            <strong>{preview ? 'Replace image' : 'Choose an image'}</strong>
            <span>JPG, PNG, GIF or WEBP • Max 5MB</span>
          </label>
          <input
            ref={fileInputRef}
            id="popup-image"
            className="popup-upload__input"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
          />
          {preview && (
            <button type="button" className="popup-remove" onClick={clearImage}>
              <i className="fas fa-trash" aria-hidden="true" /> Remove image
            </button>
          )}
        </div>

        <button type="submit" className="popup-save" disabled={saving}>
          {saving ? 'Saving...' : 'Save pop-up'}
        </button>
      </section>

      <section className="popup-settings__preview" aria-label="Popup preview">
        <div className="popup-preview__label">Preview</div>
        <div className={`popup-preview__stage position-${settings.position} ${settings.blur_background ? 'is-blurred' : ''}`}>
          <div className="popup-preview__page" aria-hidden="true">
            <div /><div /><div />
          </div>
          {preview ? (
            <div className="popup-preview__dialog">
              <span className="popup-preview__close" aria-hidden="true">×</span>
              <img src={preview} alt="Current pop-up preview" />
            </div>
          ) : (
            <div className="popup-preview__empty">
              <i className="fas fa-image" aria-hidden="true" />
              <span>Upload an image to preview the pop-up</span>
            </div>
          )}
        </div>
        <p className={`popup-status ${settings.enabled && preview ? 'is-active' : ''}`}>
          <span aria-hidden="true" />
          {settings.enabled && preview ? 'Pop-up is active' : 'Pop-up is inactive'}
        </p>
      </section>
    </form>
  );
}
